import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  IoTrendingUpOutline,
  IoTrendingDownOutline,
  IoAnalyticsOutline,
  IoLocationOutline,
  IoPeopleOutline,
  IoLayersOutline,
  IoWarningOutline,
  IoCheckmarkCircleOutline,
  IoInformationCircleOutline,
  IoArrowUpOutline,
  IoArrowDownOutline,
  IoRemoveOutline,
  IoEyeOutline,
  IoBarChartOutline,
  IoStatsChartOutline,
  IoBusinessOutline,
  IoHelpCircleOutline
} from 'react-icons/io5';

// Info Popover Component for showing calculation formulas
const InfoPopover = ({ title, formula, isOpen, onToggle }) => {
  return (
    <div className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="text-gray-400 hover:text-gray-600 transition-colors ml-1"
      >
        <IoHelpCircleOutline className="w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={onToggle}
          ></div>
          <div className="absolute right-0 top-5 z-20 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
            <h6 className="font-medium text-gray-800 text-xs mb-2">{title}</h6>
            <div className="text-xs text-gray-600 space-y-2">
              {formula.map((line, idx) => (
                <div key={idx} className={line.type === 'formula' ? 'font-mono bg-gray-50 p-1 rounded' : ''}>
                  {line.text}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Modal Component for detailed evidence
const EvidenceModal = ({ isOpen, onClose, title, data, category, sentimentFilter = 'all' }) => {
  if (!isOpen) return null;

  // Filter feedback samples based on sentiment filter
  const getFilteredFeedback = (feedbackSamples) => {
    if (!feedbackSamples || !Array.isArray(feedbackSamples)) return [];
    
    if (sentimentFilter === 'all') return feedbackSamples;
    if (sentimentFilter === 'positive') return feedbackSamples.filter(fb => fb.sentiment === 'positive');
    if (sentimentFilter === 'negative') return feedbackSamples.filter(fb => fb.sentiment === 'negative');
    if (sentimentFilter === 'neutral') return feedbackSamples.filter(fb => fb.sentiment === 'neutral');
    
    return feedbackSamples;
  };

  const filteredFeedback = getFilteredFeedback(data.feedbackSamples);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden mx-4">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-bold text-gray-800">{title}</h3>
            {sentimentFilter !== 'all' && (
              <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                sentimentFilter === 'positive' ? 'bg-green-100 text-green-800' :
                sentimentFilter === 'negative' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {sentimentFilter.charAt(0).toUpperCase() + sentimentFilter.slice(1)} Filter Applied
              </span>
            )}
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {data.subcategories && (
              <div>
                <h4 className="text-lg font-semibold mb-4 text-gray-800">Subcategory Performance Details</h4>
                
                {/* Standard Subcategories Section */}
                {data.subcategories.filter(sub => sub.type === 'predefined').length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-md font-medium mb-3 text-gray-700 border-b border-gray-200 pb-2">
                      Standard Subcategories ({data.subcategories.filter(sub => sub.type === 'predefined').length})
                    </h5>
                    <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                      {data.subcategories.filter(sub => sub.type === 'predefined').map((subcat, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border">
                          <div className="flex-1">
                            <div className="flex items-center mb-1">
                              <h5 className="font-medium text-gray-800 mr-2">{subcat.name}</h5>
                            </div>
                            
                            <div className="flex items-center space-x-4 text-sm">
                              <span className="text-green-600">+{subcat.positive} positive</span>
                              <span className="text-yellow-600">~{subcat.neutral} neutral</span>
                              <span className="text-red-600">-{subcat.negative} negative</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                              {subcat.count} total mentions
                            </span>
                            <span className={`text-sm px-3 py-1 rounded-full font-medium ${
                              subcat.positivePercentage > 60 
                                ? 'bg-green-100 text-green-800'
                                : subcat.positivePercentage < 40 
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {subcat.positivePercentage}% positive
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Subcategories Section */}
                {data.subcategories.filter(sub => sub.type === 'other').length > 0 && (
                  <div>
                    <h5 className="text-md font-medium mb-3 text-gray-700 border-b border-gray-200 pb-2">
                      Additional Detected Subcategories ({data.subcategories.filter(sub => sub.type === 'other').length})
                    </h5>
                    <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                      {data.subcategories.filter(sub => sub.type === 'other').map((subcat, idx) => (
                        <div key={idx} className="flex justify-between items-center p-4 bg-orange-50 border-orange-200 rounded-lg border">
                          <div className="flex-1">
                            <div className="flex items-center mb-1">
                              <h5 className="font-medium text-gray-800 mr-2">{subcat.name}</h5>
                            </div>
                            
                            {subcat.type === 'other' && subcat.reason && (
                              <p className="text-xs text-gray-600 mb-2 italic">
                                <strong>Detection reason:</strong> {subcat.reason}
                              </p>
                            )}
                            
                            {subcat.type === 'other' && subcat.evidenceList && subcat.evidenceList.length > 0 && (
                              <div className="mb-2">
                                <h6 className="text-xs font-medium text-gray-700 mb-1">
                                  Detection Evidence ({subcat.evidenceList.length} sample{subcat.evidenceList.length > 1 ? 's' : ''}):
                                </h6>
                                <div className="space-y-1 max-h-24 overflow-y-auto">
                                  {subcat.evidenceList.map((evidence, evidenceIdx) => (
                                    <div key={evidenceIdx} className="p-2 bg-orange-100 rounded text-xs text-gray-700 border-l-2 border-orange-400">
                                      "{evidence}"
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center space-x-4 text-sm">
                              <span className="text-green-600">+{subcat.positive} positive</span>
                              <span className="text-yellow-600">~{subcat.neutral} neutral</span>
                              <span className="text-red-600">-{subcat.negative} negative</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                              {subcat.count} total mentions
                            </span>
                            <span className={`text-sm px-3 py-1 rounded-full font-medium ${
                              subcat.positivePercentage > 60 
                                ? 'bg-green-100 text-green-800'
                                : subcat.positivePercentage < 40 
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {subcat.positivePercentage}% positive
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {data.feedbackSamples && data.feedbackSamples.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">
                    Customer Feedback 
                    {sentimentFilter !== 'all' && (
                      <span className="ml-2 text-sm font-normal text-gray-600">
                        ({filteredFeedback.length} of {data.feedbackSamples.length} responses - {sentimentFilter} filter)
                      </span>
                    )}
                    {sentimentFilter === 'all' && (
                      <span className="ml-2 text-sm font-normal text-gray-600">
                        ({data.feedbackSamples.length} total responses)
                      </span>
                    )}
                  </h4>
                  {filteredFeedback.length === 0 && sentimentFilter !== 'all' && (
                    <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded">
                      No {sentimentFilter} feedback found
                    </div>
                  )}
                </div>
                
                {filteredFeedback.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredFeedback.map((feedback, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                        <div className="flex justify-between items-start mb-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            feedback.sentiment === 'positive' 
                              ? 'bg-green-100 text-green-800'
                              : feedback.sentiment === 'negative'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {feedback.sentiment.toUpperCase()}
                          </span>
                          {feedback.demographics && (
                            <div className="text-xs text-gray-500 flex items-center space-x-2">
                              <span>👤 {feedback.demographics.age} years, {feedback.demographics.gender}</span>
                              {feedback.location && feedback.location !== 'Unknown' && (
                                <span>📍 {feedback.location}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed italic">
                          "{feedback.text}"
                        </p>
                        {feedback.interaction && feedback.interaction !== 'Unknown' && (
                          <div className="mt-2 text-xs text-gray-500">
                            💬 Via: {feedback.interaction}
                          </div>
                        )}
                        {feedback.evidence && feedback.evidence !== '' && (
                          <div className="mt-2 p-2 bg-orange-50 rounded text-xs text-gray-700 border-l-2 border-orange-300">
                            <strong>Detection Evidence:</strong> "{feedback.evidence}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  sentimentFilter !== 'all' && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="mb-2">
                        <svg className="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <p>No {sentimentFilter} feedback responses found for this selection.</p>
                      <p className="text-xs mt-1">Try selecting "All" to see all available feedback.</p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Modal Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gray-50">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main CategoryInsightsAnalysis Component
const CategoryInsightsAnalysis = ({ data }) => {
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryAnalysis, setCategoryAnalysis] = useState(null);
  const [modalState, setModalState] = useState({ isOpen: false, title: '', data: {}, category: '', sentimentFilter: 'all' });
  const [subcategoryFilter, setSubcategoryFilter] = useState('all'); // 'all', 'positive', 'negative', 'neutral'
  const [activeSubcategoryTab, setActiveSubcategoryTab] = useState('standard'); // 'standard', 'additional'

  const [activePopover, setActivePopover] = useState(null); // Track which popover is open

  // Get calculation formula for different insight types
  const getInsightFormula = (insightType) => {
    const formulas = {
      'age_satisfaction': [
        { type: 'text', text: 'Impact Level Calculation:' },
        { type: 'formula', text: 'Difference = |Highest Age % - Lowest Age %|' },
        { type: 'text', text: 'If difference > 20% → High Impact' },
        { type: 'text', text: 'If difference ≤ 20% → Moderate Impact' },
        { type: 'text', text: 'Example: |45% - 15%| = 30% → High Impact' }
      ],
      'gender_satisfaction': [
        { type: 'text', text: 'Impact Level Calculation:' },
        { type: 'formula', text: 'Gap = |Male % - Female %|' },
        { type: 'text', text: 'If gap > 25% → High Impact' },
        { type: 'text', text: 'If gap > 15% → Moderate Impact' },
        { type: 'text', text: 'If gap ≤ 15% → Low Impact' }
      ],
      'interaction_summary': [
        { type: 'text', text: 'Impact Level Calculation:' },
        { type: 'formula', text: 'Based on multiple factors:' },
        { type: 'text', text: '• High-volume channels with <30% positive' },
        { type: 'text', text: '• Overall average satisfaction <50%' },
        { type: 'text', text: '• Number of problematic channels (>2)' }
      ],
      'category_health': [
        { type: 'text', text: 'Impact Level Calculation:' },
        { type: 'formula', text: 'Based on positive sentiment %:' },
        { type: 'text', text: 'If >70% positive → Positive Impact' },
        { type: 'text', text: 'If <40% positive → Critical Impact' },
        { type: 'text', text: 'Otherwise → Moderate Impact' }
      ]
    };
    return formulas[insightType] || [{ type: 'text', text: 'Calculation details not available' }];
  };

  // Filter subcategories utility
  const filterSubcategories = (subcategories) => {
    if (!subcategories || !Array.isArray(subcategories)) return [];
    return subcategories.filter(subcat => 
      subcat.subcategory && 
      subcat.subcategory.toLowerCase() !== 'staff' &&
      subcat.subcategory !== 'staff'
    );
  };

  // Filter subcategories by sentiment performance
  const getFilteredSubcategories = (subcategories) => {
    if (subcategoryFilter === 'all') return subcategories;
    if (subcategoryFilter === 'positive') return subcategories.filter(sub => sub.positivePercentage > 60);
    if (subcategoryFilter === 'negative') return subcategories.filter(sub => sub.negativePercentage > 40);
    if (subcategoryFilter === 'neutral') return subcategories.filter(sub => sub.neutralPercentage > 40);
    return subcategories;
  };

  // Show evidence modal
  const showEvidence = (title, data, category, sentimentFilter = 'all') => {
    setModalState({ isOpen: true, title, data, category, sentimentFilter });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, title: '', data: {}, category: '', sentimentFilter: 'all' });
  };

  useEffect(() => {
    if (data && data.data) {
      analyzeCategoryInsights(data);
    }
  }, [data]);



  const analyzeCategoryInsights = (feedbackData) => {
    setLoading(true);
    
    try {
      const allFeedback = feedbackData.data || [];
      
      // Get all unique categories
      const categoriesSet = new Set();
      allFeedback.forEach(item => {
        if (item.processed_data?.categories) {
          item.processed_data.categories.forEach(cat => {
            categoriesSet.add(cat.category);
          });
        }
      });

      const categories = Array.from(categoriesSet);
      
      // Analyze each category
      const analysis = {};
      
      categories.forEach(category => {
        analysis[category] = analyzeSingleCategory(allFeedback, category);
      });

      setCategoryAnalysis(analysis);
      
      // Set first category as active (excluding "Others")
      const filteredCategories = categories.filter(category => category !== 'Others');
      if (filteredCategories.length > 0 && !activeCategory) {
        setActiveCategory(filteredCategories[0]);
      }
      
    } catch (error) {
      console.error('Error analyzing category insights:', error);
      setCategoryAnalysis(null);
    }
    
    setLoading(false);
  };

  const analyzeSingleCategory = (allFeedback, category) => {
    // Filter feedback for this category
    const categoryFeedback = allFeedback.filter(item =>
      item.processed_data?.categories?.some(cat => cat.category === category)
    );

    // Separate by sentiment
    const positiveFeedback = categoryFeedback.filter(item =>
      item.processed_data.categories.some(cat => 
        cat.category === category && cat.sentiment === 'positive'
      )
    );

    const negativeFeedback = categoryFeedback.filter(item =>
      item.processed_data.categories.some(cat => 
        cat.category === category && cat.sentiment === 'negative'
      )
    );

    const neutralFeedback = categoryFeedback.filter(item =>
      item.processed_data.categories.some(cat => 
        cat.category === category && cat.sentiment === 'neutral'
      )
    );

    // Analyze subcategories
    const subcategoryAnalysis = analyzeSubcategories(categoryFeedback, category);
    
    // Demographic analysis
    const demographicAnalysis = analyzeDemographics(categoryFeedback, category);
    
    // Interaction subcategory analysis
    const interactionAnalysis = analyzeInteractionSubcategories(categoryFeedback, category);

    const analysisResult = {
      category: category.replace(/_/g, ' '),
      totalMentions: categoryFeedback.length,
      positiveMentions: positiveFeedback.length,
      negativeMentions: negativeFeedback.length,
      neutralMentions: neutralFeedback.length,
      positivePercentage: Math.round((positiveFeedback.length / categoryFeedback.length) * 100),
      negativePercentage: Math.round((negativeFeedback.length / categoryFeedback.length) * 100),
      neutralPercentage: Math.round((neutralFeedback.length / categoryFeedback.length) * 100),
      subcategories: subcategoryAnalysis,
      demographics: demographicAnalysis,
      interactions: interactionAnalysis.interactions || interactionAnalysis,
      sentiment: positiveFeedback.length > negativeFeedback.length ? 'positive' : 'negative'
    };

    // Generate AI insights
    analysisResult.overallInsights = generateOverallInsights(analysisResult);
    analysisResult.demographicInsights = generateDemographicInsights(demographicAnalysis, analysisResult.category);
    analysisResult.interactionInsights = generateInteractionInsights(interactionAnalysis, analysisResult.category);

    return analysisResult;
  };

  const analyzeSubcategories = (categoryFeedback, category) => {
    const subcategoryMap = {};
    const otherSubcategoryMap = {};
    
    categoryFeedback.forEach(item => {
      // Process regular subcategories
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        filteredSubcategories.forEach(subcat => {
          if (subcat.category === category && subcat.subcategory !== 'Others') {
            const name = subcat.subcategory.replace(/_/g, ' ');
            if (!subcategoryMap[name]) {
              subcategoryMap[name] = {
                name,
                total: 0,
                positive: 0,
                negative: 0,
                neutral: 0,
                type: 'predefined'
              };
            }
            
            subcategoryMap[name].total++;
            subcategoryMap[name][subcat.sentiment]++;
          }
        });
      }

      // Process otherSubcategories
      if (item.processed_data?.otherSubcategories) {
        item.processed_data.otherSubcategories.forEach(otherSubcat => {
          if (otherSubcat.category === category && otherSubcat.subcategory !== 'Others') {
            const name = otherSubcat.subcategory.replace(/_/g, ' ');
            
            // Determine sentiment from the main category sentiment for this item
            const categoryData = item.processed_data.categories?.find(cat => cat.category === category);
            const sentiment = categoryData?.sentiment || 'neutral';
            
            if (!otherSubcategoryMap[name]) {
              otherSubcategoryMap[name] = {
                name,
                total: 0,
                positive: 0,
                negative: 0,
                neutral: 0,
                evidenceList: [],
                reason: otherSubcat.reason || '',
                type: 'other'
              };
            }
            
            // Store evidence if not already stored
            if (otherSubcat.evidence && !otherSubcategoryMap[name].evidenceList.includes(otherSubcat.evidence)) {
              otherSubcategoryMap[name].evidenceList.push(otherSubcat.evidence);
            }
            
            otherSubcategoryMap[name].total++;
            otherSubcategoryMap[name][sentiment]++;
          }
        });
      }
    });

    // Combine and process all subcategories
    const allSubcategories = [
      ...Object.values(subcategoryMap),
      ...Object.values(otherSubcategoryMap)
    ];

    return allSubcategories
      .map(subcat => ({
        ...subcat,
        positivePercentage: Math.round((subcat.positive / subcat.total) * 100),
        negativePercentage: Math.round((subcat.negative / subcat.total) * 100),
        neutralPercentage: Math.round((subcat.neutral / subcat.total) * 100),
        count: subcat.total
      }))
      .sort((a, b) => b.total - a.total);
  };

  // Generate AI insights for demographics
  const generateDemographicInsights = (demographicData, categoryName) => {
    const insights = [];
    
    // Find most satisfied and dissatisfied age groups
    const ageGroupsByPositive = demographicData.ageGroupSatisfaction
      .sort((a, b) => b.positivePercentage - a.positivePercentage);
    
    const mostSatisfiedAge = ageGroupsByPositive[0];
    const leastSatisfiedAge = ageGroupsByPositive[ageGroupsByPositive.length - 1];
    
    if (mostSatisfiedAge && leastSatisfiedAge && mostSatisfiedAge.ageGroup !== leastSatisfiedAge.ageGroup) {
      // Find top performing subcategories for most satisfied age group
      const mostSatisfiedSubcats = Object.entries(demographicData.ageSubcategories[mostSatisfiedAge.ageGroup] || {})
        .filter(([_, data]) => data.positive > 0)
        .sort((a, b) => b[1].positive - a[1].positive)
        .slice(0, 2)
        .map(([name, data]) => `${name} (+${data.positive})`);
      
      // Find problem subcategories for least satisfied age group
      const leastSatisfiedSubcats = Object.entries(demographicData.ageSubcategories[leastSatisfiedAge.ageGroup] || {})
        .filter(([_, data]) => data.negative > 0)
        .sort((a, b) => b[1].negative - a[1].negative)
        .slice(0, 2)
        .map(([name, data]) => `${name} (-${data.negative})`);
      
      let insightText = `${mostSatisfiedAge.ageGroup} customers show highest satisfaction with ${categoryName} (${mostSatisfiedAge.positivePercentage}% positive), while ${leastSatisfiedAge.ageGroup} customers show lowest satisfaction (${leastSatisfiedAge.positivePercentage}% positive).`;
      
      if (mostSatisfiedSubcats.length > 0) {
        insightText += ` Top areas for ${mostSatisfiedAge.ageGroup}: ${mostSatisfiedSubcats.join(', ')}.`;
      }
      
      if (leastSatisfiedSubcats.length > 0) {
        insightText += ` Problem areas for ${leastSatisfiedAge.ageGroup}: ${leastSatisfiedSubcats.join(', ')}.`;
      }
      
      insights.push({
        type: 'age_satisfaction',
        title: 'Age Group Satisfaction Pattern',
        insight: insightText,
        impact: Math.abs(mostSatisfiedAge.positivePercentage - leastSatisfiedAge.positivePercentage) > 20 ? 'High' : 'Moderate'
      });
    }
    
    // Find gender satisfaction patterns
    const genderByPositive = demographicData.genderSatisfaction
      .sort((a, b) => b.positivePercentage - a.positivePercentage);
    
    if (genderByPositive.length === 2) {
      const satisfactionGap = Math.abs(genderByPositive[0].positivePercentage - genderByPositive[1].positivePercentage);
      if (satisfactionGap > 15) {
        insights.push({
          type: 'gender_satisfaction',
          title: 'Gender Satisfaction Gap',
          insight: `${genderByPositive[0].gender} customers are significantly more satisfied with ${categoryName} (${genderByPositive[0].positivePercentage}% vs ${genderByPositive[1].positivePercentage}% positive).`,
          impact: satisfactionGap > 25 ? 'High' : 'Moderate'
        });
      }
    }

    // Add subcategory insights for demographics
    if (demographicData.subcategoryInsights) {
      demographicData.subcategoryInsights.forEach(insight => {
        insights.push(insight);
      });
    }
    
    return insights;
  };

  // Generate AI insights for interaction subcategories
  const generateInteractionInsights = (interactionData, categoryName) => {
    const insights = [];
    
    // Handle the new data structure - interactionData is now an object with interactions array
    const interactions = interactionData.interactions || interactionData;
    
    // Find best and worst performing interaction channels
    const interactionsByPositive = interactions
      .sort((a, b) => b.positivePercentage - a.positivePercentage);
    
    if (interactionsByPositive.length > 0) {
      const bestChannel = interactionsByPositive[0];
      const worstChannel = interactionsByPositive[interactionsByPositive.length - 1];
      const totalChannels = interactionsByPositive.length;
      const avgSatisfaction = Math.round(interactionsByPositive.reduce((sum, ch) => sum + ch.positivePercentage, 0) / totalChannels);
      
      // Find high volume low satisfaction channels
      const problematicChannels = interactions.filter(channel => 
        channel.count > 10 && channel.positivePercentage < 40
      );
      
      let insightText = `Analysis of ${totalChannels} interaction channels shows average satisfaction of ${avgSatisfaction}%. `;
      
      if (bestChannel.interaction !== worstChannel.interaction) {
        insightText += `Best performer: ${bestChannel.interaction} (${bestChannel.positivePercentage}%), `;
        insightText += `needs attention: ${worstChannel.interaction} (${worstChannel.positivePercentage}%).`;
      }
      
      if (problematicChannels.length > 0) {
        insightText += ` ${problematicChannels.length} high-volume channels show concerning satisfaction levels.`;
      }
      
      insights.push({
        type: 'interaction_summary',
        title: 'Interaction Channel Performance',
        insight: insightText,
        impact: problematicChannels.length > 2 || worstChannel.positivePercentage < 30 ? 'High' : 
                avgSatisfaction < 50 ? 'Moderate' : 'Positive'
      });
    }

    // Add subcategory insights for interactions
    if (interactionData.subcategoryInsights) {
      interactionData.subcategoryInsights.forEach(insight => {
        insights.push(insight);
      });
    }
    
    return insights;
  };

  // Generate overall AI insights for the category
  const generateOverallInsights = (categoryData) => {
    const insights = [];
    
    // Overall category health
    if (categoryData.positivePercentage > 70) {
      insights.push({
        type: 'category_health',
        title: 'Strong Category Performance',
        insight: `${categoryData.category} shows strong overall performance with ${categoryData.positivePercentage}% positive sentiment across ${categoryData.totalMentions} customer mentions.`,
        impact: 'Positive',
        actionable: 'Maintain current standards and identify best practices to replicate across other categories.'
      });
    } else if (categoryData.positivePercentage < 40) {
      insights.push({
        type: 'category_health',
        title: 'Category Needs Attention',
        insight: `${categoryData.category} requires immediate attention with only ${categoryData.positivePercentage}% positive sentiment across ${categoryData.totalMentions} customer mentions.`,
        impact: 'Critical',
        actionable: 'Conduct comprehensive review of processes, training, and customer touchpoints for this category.'
      });
    }
    
    // Subcategory diversity insights
    const totalSubcategories = categoryData.subcategories.length;
    const otherSubcategories = categoryData.subcategories.filter(sub => sub.type === 'other').length;
    
    if (otherSubcategories > 0) {
      insights.push({
        type: 'subcategory_diversity',
        title: 'Additional Categories Detected',
        insight: `${otherSubcategories} additional subcategories were detected beyond the standard ${totalSubcategories - otherSubcategories}, indicating evolving customer needs in ${categoryData.category}.`,
        impact: 'Moderate',
        actionable: 'Review additional subcategories to update service taxonomy and training materials.'
      });
    }
    
    return insights;
  };

  const analyzeDemographics = (categoryFeedback, category) => {
    const ageGroups = { 'Under 30': 0, '30-50': 0, 'Over 50': 0 };
    const genderDistribution = { Male: 0, Female: 0 };
    const ageSentiments = { 
      'Under 30': { positive: 0, negative: 0, neutral: 0 },
      '30-50': { positive: 0, negative: 0, neutral: 0 },
      'Over 50': { positive: 0, negative: 0, neutral: 0 }
    };
    const genderSentiments = {
      Male: { positive: 0, negative: 0, neutral: 0 },
      Female: { positive: 0, negative: 0, neutral: 0 }
    };

    // Track subcategories by demographics
    const ageSubcategories = {
      'Under 30': {},
      '30-50': {},
      'Over 50': {}
    };
    const genderSubcategories = {
      Male: {},
      Female: {}
    };
    
    categoryFeedback.forEach(item => {
      const age = parseInt(item.age) || 0;
      const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
      const gender = item.gender === 'M' ? 'Male' : 'Female';
      
      // Get sentiment for this category
      const categoryData = item.processed_data.categories.find(cat => cat.category === category);
      const sentiment = categoryData?.sentiment || 'neutral';
      
      ageGroups[ageGroup]++;
      genderDistribution[gender]++;
      
      // Track sentiment by demographics
      ageSentiments[ageGroup][sentiment]++;
      genderSentiments[gender][sentiment]++;

      // Track subcategories by demographics
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        filteredSubcategories.forEach(subcat => {
          if (subcat.category === category && subcat.subcategory !== 'Others') {
            const subcatName = subcat.subcategory.replace(/_/g, ' ');
            
            // Age group subcategories
            if (!ageSubcategories[ageGroup][subcatName]) {
              ageSubcategories[ageGroup][subcatName] = { positive: 0, negative: 0, neutral: 0, total: 0 };
            }
            ageSubcategories[ageGroup][subcatName][subcat.sentiment]++;
            ageSubcategories[ageGroup][subcatName].total++;

            // Gender subcategories
            if (!genderSubcategories[gender][subcatName]) {
              genderSubcategories[gender][subcatName] = { positive: 0, negative: 0, neutral: 0, total: 0 };
            }
            genderSubcategories[gender][subcatName][subcat.sentiment]++;
            genderSubcategories[gender][subcatName].total++;
          }
        });
      }


    });

    const totalFeedback = categoryFeedback.length;
    const dominantAge = Object.entries(ageGroups).sort((a, b) => b[1] - a[1])[0];
    const dominantGender = Object.entries(genderDistribution).sort((a, b) => b[1] - a[1])[0];

    // Calculate satisfaction percentages for each age group
    const ageGroupSatisfaction = Object.entries(ageSentiments).map(([ageGroup, sentiments]) => {
      const total = sentiments.positive + sentiments.negative + sentiments.neutral;
      return {
        ageGroup,
        total,
        positive: sentiments.positive,
        negative: sentiments.negative,
        neutral: sentiments.neutral,
        positivePercentage: total > 0 ? Math.round((sentiments.positive / total) * 100) : 0,
        negativePercentage: total > 0 ? Math.round((sentiments.negative / total) * 100) : 0,
        neutralPercentage: total > 0 ? Math.round((sentiments.neutral / total) * 100) : 0
      };
    });

    // Calculate satisfaction percentages for each gender
    const genderSatisfaction = Object.entries(genderSentiments).map(([gender, sentiments]) => {
      const total = sentiments.positive + sentiments.negative + sentiments.neutral;
      return {
        gender,
        total,
        positive: sentiments.positive,
        negative: sentiments.negative,
        neutral: sentiments.neutral,
        positivePercentage: total > 0 ? Math.round((sentiments.positive / total) * 100) : 0,
        negativePercentage: total > 0 ? Math.round((sentiments.negative / total) * 100) : 0,
        neutralPercentage: total > 0 ? Math.round((sentiments.neutral / total) * 100) : 0
      };
    });

    // Generate subcategory insights for demographics
    const subcategoryInsights = generateDemographicSubcategoryInsights(ageSubcategories, genderSubcategories, category);

    return {
      ageGroups,
      genderDistribution,
      totalFeedback,
      ageGroupSatisfaction,
      genderSatisfaction,
      dominantAge: dominantAge ? {
        group: dominantAge[0],
        count: dominantAge[1],
        percentage: Math.round((dominantAge[1] / totalFeedback) * 100)
      } : null,
      dominantGender: dominantGender ? {
        gender: dominantGender[0],
        count: dominantGender[1],
        percentage: Math.round((dominantGender[1] / totalFeedback) * 100)
      } : null,
      ageSubcategories,
      genderSubcategories,
      subcategoryInsights
    };
  };

  // Generate AI insights for demographics
  const generateDemographicSubcategoryInsights = (ageSubcategories, genderSubcategories, categoryName) => {
    const insights = [];

    // Age Group Insights
    Object.entries(ageSubcategories).forEach(([ageGroup, subcategories]) => {
      const positiveSubcategories = Object.entries(subcategories).filter(([_, data]) => data.positive > 0);
      const negativeSubcategories = Object.entries(subcategories).filter(([_, data]) => data.negative > 0);

      if (positiveSubcategories.length > 0) {
        const topPerformers = positiveSubcategories.sort((a, b) => b[1].positive - a[1].positive).slice(0, 2);
        const worstPerformers = negativeSubcategories.sort((a, b) => b[1].negative - a[1].negative).slice(0, 2);

        if (topPerformers.length > 0) {
          insights.push({
            type: 'subcategory_performance_age',
            title: `${ageGroup} Key Themes`,
            insight: `Best: ${topPerformers.map(s => `${s[0]} (+${s[1].positive})`).join(', ')}${worstPerformers.length > 0 ? ` | Worst: ${worstPerformers.map(s => `${s[0]} (-${s[1].negative})`).join(', ')}` : ''}`,
            impact: 'Moderate'
          });
        }
      }
    });

    // Gender Insights
    Object.entries(genderSubcategories).forEach(([gender, subcategories]) => {
      const positiveSubcategories = Object.entries(subcategories).filter(([_, data]) => data.positive > 0);
      const negativeSubcategories = Object.entries(subcategories).filter(([_, data]) => data.negative > 0);

      if (positiveSubcategories.length > 0) {
        const topPerformers = positiveSubcategories.sort((a, b) => b[1].positive - a[1].positive).slice(0, 2);
        const worstPerformers = negativeSubcategories.sort((a, b) => b[1].negative - a[1].negative).slice(0, 2);

        if (topPerformers.length > 0) {
          insights.push({
            type: 'subcategory_performance_gender',
            title: `${gender} Key Themes`,
            insight: `Best: ${topPerformers.map(s => `${s[0]} (+${s[1].positive})`).join(', ')}${worstPerformers.length > 0 ? ` | Worst: ${worstPerformers.map(s => `${s[0]} (-${s[1].negative})`).join(', ')}` : ''}`,
            impact: 'Moderate'
          });
        }
      }
    });

    return insights;
  };

  const analyzeInteractionSubcategories = (categoryFeedback, category) => {
    const interactionMap = {};
    const interactionSentiments = {};
    
    // Track subcategories by interaction types
    const interactionSubcategories = {};
    
    categoryFeedback.forEach(item => {
      const interaction = item.interactionSubCategory || 'Unknown';
      
      // Get sentiment for this category
      const categoryData = item.processed_data.categories.find(cat => cat.category === category);
      const sentiment = categoryData?.sentiment || 'neutral';
      
      interactionMap[interaction] = (interactionMap[interaction] || 0) + 1;
      
      if (!interactionSentiments[interaction]) {
        interactionSentiments[interaction] = { positive: 0, negative: 0, neutral: 0 };
      }
      
      interactionSentiments[interaction][sentiment]++;

      // Track subcategories by interaction
      if (!interactionSubcategories[interaction]) {
        interactionSubcategories[interaction] = {};
      }

      // Track regular subcategories
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        filteredSubcategories.forEach(subcat => {
          if (subcat.category === category && subcat.subcategory !== 'Others') {
            const subcatName = subcat.subcategory.replace(/_/g, ' ');
            
            if (!interactionSubcategories[interaction][subcatName]) {
              interactionSubcategories[interaction][subcatName] = { positive: 0, negative: 0, neutral: 0, total: 0 };
            }
            interactionSubcategories[interaction][subcatName][subcat.sentiment]++;
            interactionSubcategories[interaction][subcatName].total++;
          }
        });
      }


    });

    const totalFeedback = categoryFeedback.length;
    const topInteractions = Object.entries(interactionMap)
      .sort((a, b) => b[1] - a[1])
      .map(([interaction, count]) => {
        const sentiments = interactionSentiments[interaction];
        const positivePercentage = Math.round((sentiments.positive / count) * 100);
        const negativePercentage = Math.round((sentiments.negative / count) * 100);
        const neutralPercentage = Math.round((sentiments.neutral / count) * 100);
        
        return {
          interaction,
          count,
          percentage: Math.round((count / totalFeedback) * 100),
          positive: sentiments.positive,
          negative: sentiments.negative,
          neutral: sentiments.neutral,
          positivePercentage,
          negativePercentage,
          neutralPercentage,
          satisfactionLevel: positivePercentage > 60 ? 'High' : positivePercentage > 40 ? 'Medium' : 'Low'
        };
      });

    // Generate subcategory insights for interactions
    const subcategoryInsights = generateInteractionSubcategoryInsights(interactionSubcategories, category);

    return {
      interactions: topInteractions,
      subcategoryInsights,
      interactionSubcategories
    };
  };

  // Generate AI insights for interaction subcategories
  const generateInteractionSubcategoryInsights = (interactionSubcategories, categoryName) => {
    const insights = [];

    // Iterate through interaction types
    Object.entries(interactionSubcategories).forEach(([interaction, subcategories]) => {
      const positiveSubcategories = Object.entries(subcategories).filter(([_, data]) => data.positive > 0);
      const negativeSubcategories = Object.entries(subcategories).filter(([_, data]) => data.negative > 0);

      if (positiveSubcategories.length > 0) {
        const topPerformers = positiveSubcategories.sort((a, b) => b[1].positive - a[1].positive).slice(0, 2);
        const worstPerformers = negativeSubcategories.sort((a, b) => b[1].negative - a[1].negative).slice(0, 2);

        if (topPerformers.length > 0) {
          insights.push({
            type: 'subcategory_performance_interaction',
            title: `${interaction} Key Themes`,
            insight: `Best: ${topPerformers.map(s => `${s[0]} (+${s[1].positive})`).join(', ')}${worstPerformers.length > 0 ? ` | Worst: ${worstPerformers.map(s => `${s[0]} (-${s[1].negative})`).join(', ')}` : ''}`,
            impact: 'Moderate'
          });
        }
      }
    });

    return insights;
  };

  // Get all feedback for a specific subcategory from original data
  const getFeedbackForSubcategory = (categoryName, subcategoryName, subcategoryType = 'predefined') => {
    if (!data || !data.data) return [];

    const allFeedback = data.data;
    const matchingFeedback = [];

    allFeedback.forEach(item => {
      let foundMatch = false;

      // Check regular subcategories
      if (subcategoryType === 'predefined' && item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        filteredSubcategories.forEach(subcat => {
          if (subcat.category === categoryName && 
              subcat.subcategory !== 'Others' &&
              subcat.subcategory.replace(/_/g, ' ') === subcategoryName) {
            foundMatch = true;
          }
        });
      }

      // Check otherSubcategories
      if (subcategoryType === 'other' && item.processed_data?.otherSubcategories) {
        item.processed_data.otherSubcategories.forEach(otherSubcat => {
          if (otherSubcat.category === categoryName && 
              otherSubcat.subcategory !== 'Others' &&
              otherSubcat.subcategory.replace(/_/g, ' ') === subcategoryName) {
            foundMatch = true;
          }
        });
      }

      if (foundMatch) {
        // Get sentiment for this category from the item
        const categoryData = item.processed_data.categories?.find(cat => cat.category === categoryName);
        const sentiment = categoryData?.sentiment || 'neutral';

        matchingFeedback.push({
          text: item.text,
          sentiment: sentiment,
          demographics: {
            age: item.age || 'Unknown',
            gender: item.gender === 'M' ? 'Male' : item.gender === 'F' ? 'Female' : 'Unknown'
          },
          location: item.circle || item.city || 'Unknown',
          interaction: item.interactionSubCategory || 'Unknown',
          caseClassification: item.caseClassification || 'Unknown',
          // For other subcategories, include evidence if available
          evidence: subcategoryType === 'other' && item.processed_data?.otherSubcategories ? 
            item.processed_data.otherSubcategories.find(os => 
              os.category === categoryName && os.subcategory.replace(/_/g, ' ') === subcategoryName
            )?.evidence || '' : '',
          reason: subcategoryType === 'other' && item.processed_data?.otherSubcategories ? 
            item.processed_data.otherSubcategories.find(os => 
              os.category === categoryName && os.subcategory.replace(/_/g, ' ') === subcategoryName
            )?.reason || '' : ''
        });
      }
    });

    return matchingFeedback;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!categoryAnalysis) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="text-center py-8 text-gray-500">
          Unable to analyze category insights. Please check the data.
        </div>
      </div>
    );
  }

  // Sort categories by total mentions in descending order, then alphabetically
  const categories = Object.keys(categoryAnalysis)
    .filter(category => category !== 'Others') // Filter out "Others" category
    .sort((a, b) => {
      const analysisA = categoryAnalysis[a];
      const analysisB = categoryAnalysis[b];
      
      // First sort by total mentions (descending)
      if (analysisB.totalMentions !== analysisA.totalMentions) {
        return analysisB.totalMentions - analysisA.totalMentions;
      }
      
      // If mentions are equal, sort alphabetically
      return a.localeCompare(b);
    });
  
  const currentAnalysis = activeCategory ? categoryAnalysis[activeCategory] : null;

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <EvidenceModal 
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        data={modalState.data}
        category={modalState.category}
        sentimentFilter={modalState.sentimentFilter}
      />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <IoLayersOutline className="w-8 h-8 text-indigo-600 mr-3" />
          <div>
            <h3 className="text-xl font-bold text-gray-800">Category Insights Analysis</h3>
            <p className="text-sm text-gray-600">
              Comprehensive analysis across all categories with performance metrics and insights
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-2 overflow-x-auto">
            {categories.map((category) => {
              const analysis = categoryAnalysis[category];
              const isActive = activeCategory === category;
              
              return (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`group inline-flex items-center py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                    isActive
                      ? analysis.sentiment === 'positive'
                        ? 'border-green-500 text-green-600'
                        : 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="truncate max-w-32">
                    {category.replace(/_/g, ' ')}
                  </span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    isActive 
                      ? analysis.sentiment === 'positive'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {analysis.totalMentions}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      

      {currentAnalysis && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total Mentions</p>
                  <p className="text-2xl font-bold text-blue-900">{currentAnalysis.totalMentions}</p>
                </div>
                <IoBarChartOutline className="w-8 h-8 text-blue-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Positive</p>
                  <p className="text-2xl font-bold text-green-900">{currentAnalysis.positivePercentage}%</p>
                  <p className="text-xs text-green-600">{currentAnalysis.positiveMentions} mentions</p>
                </div>
                <IoCheckmarkCircleOutline className="w-8 h-8 text-green-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Negative</p>
                  <p className="text-2xl font-bold text-red-900">{currentAnalysis.negativePercentage}%</p>
                  <p className="text-xs text-red-600">{currentAnalysis.negativeMentions} mentions</p>
                </div>
                <IoWarningOutline className="w-8 h-8 text-red-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 font-medium">Neutral</p>
                  <p className="text-2xl font-bold text-yellow-900">{currentAnalysis.neutralPercentage}%</p>
                  <p className="text-xs text-yellow-600">{currentAnalysis.neutralMentions} mentions</p>
                </div>
                <IoInformationCircleOutline className="w-8 h-8 text-yellow-500" />
              </div>
            </motion.div>
          </div>

          {/* Overall AI Insights */}
          {currentAnalysis.overallInsights && currentAnalysis.overallInsights.length > 0 && (
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <div className="flex items-center mb-3">
                <IoAnalyticsOutline className="w-5 h-5 text-indigo-600 mr-2" />
                <h4 className="text-lg font-semibold text-gray-800">Category Insights</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentAnalysis.overallInsights.map((insight, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg border border-indigo-100">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-800 text-sm">{insight.title}</h5>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        insight.impact === 'Critical' ? 'bg-red-100 text-red-700' :
                        insight.impact === 'High' ? 'bg-orange-100 text-orange-700' :
                        insight.impact === 'Positive' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {insight.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{insight.insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subcategory Performance */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Subcategory Performance</h4>
              <div className="flex items-center space-x-2">
                {/* Filter Buttons */}
                <div className="flex bg-white rounded-lg border border-gray-200 p-1">
                  {[
                    { key: 'all', label: 'All', color: 'gray' },
                    { key: 'positive', label: 'Mostly Positive', color: 'green' },
                    { key: 'negative', label: 'Mostly Negative', color: 'red' },
                    { key: 'neutral', label: 'Mostly Neutral', color: 'yellow' }
                  ].map((filter) => (
                    <button
                      key={filter.key}
                      onClick={() => setSubcategoryFilter(filter.key)}
                      className={`px-3 py-1 text-xs rounded-md transition-all ${
                        subcategoryFilter === filter.key
                          ? filter.color === 'green' 
                            ? 'bg-green-100 text-green-700 font-medium'
                            : filter.color === 'red'
                            ? 'bg-red-100 text-red-700 font-medium'
                            : filter.color === 'yellow'
                            ? 'bg-yellow-100 text-yellow-700 font-medium'
                            : 'bg-gray-100 text-gray-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => showEvidence(
                    `${currentAnalysis.category} - Subcategory Analysis`,
                    { 
                      subcategories: [
                        ...currentAnalysis.subcategories.filter(sub => sub.type === 'predefined'),
                        ...currentAnalysis.subcategories.filter(sub => sub.type === 'other')
                      ]
                    },
                    activeCategory,
                    subcategoryFilter
                  )}
                  className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  <IoEyeOutline className="w-4 h-4" />
                  <span>View Details</span>
                </button>
              </div>
            </div>

            {/* Subcategory Tabs */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="-mb-px flex space-x-8">
                {[
                  { 
                    key: 'standard', 
                    label: 'Standard Subcategories', 
                    count: currentAnalysis.subcategories.filter(sub => sub.type === 'predefined').length 
                  },
                  { 
                    key: 'additional', 
                    label: 'Other Categories Detected', 
                    count: currentAnalysis.subcategories.filter(sub => sub.type === 'other').length 
                  }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveSubcategoryTab(tab.key)}
                    className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      activeSubcategoryTab === tab.key
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                        activeSubcategoryTab === tab.key 
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
            
            {/* Subcategory Content */}
            {activeSubcategoryTab === 'standard' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {getFilteredSubcategories(currentAnalysis.subcategories.filter(sub => sub.type === 'predefined')).map((subcat, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white p-3 rounded-lg border border-gray-200 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => showEvidence(
                      `${subcat.name} - Detailed Analysis`,
                      { subcategories: [subcat], feedbackSamples: getFeedbackForSubcategory(activeCategory, subcat.name, 'predefined') },
                      activeCategory,
                      subcategoryFilter
                    )}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-800 text-sm">{subcat.name}</h5>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {subcat.total}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${subcat.positivePercentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{subcat.positivePercentage}%</span>
                    </div>
                    
                    <div className="flex justify-between text-xs text-gray-500">
                      <span className="text-green-600">+{subcat.positive}</span>
                      <span className="text-gray-600">{subcat.neutral}</span>
                      <span className="text-red-600">-{subcat.negative}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {activeSubcategoryTab === 'additional' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {getFilteredSubcategories(currentAnalysis.subcategories.filter(sub => sub.type === 'other')).map((subcat, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white p-4 rounded-lg border border-orange-200 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => showEvidence(
                      `${subcat.name} - Additional Subcategory`,
                      { subcategories: [subcat], feedbackSamples: getFeedbackForSubcategory(activeCategory, subcat.name, 'other') },
                      activeCategory,
                      subcategoryFilter
                    )}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-800 text-sm mb-1">{subcat.name}</h5>
                        {/* {subcat.reason && (
                          <p className="text-xs text-gray-600 mb-2 italic">
                            {subcat.reason}
                          </p>
                        )} */}
                      </div>
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        {subcat.total}
                      </span>
                    </div>
                    
                    {subcat.evidenceList && subcat.evidenceList.length > 0 && (
                      <div className="mb-3 p-2 bg-orange-50 rounded text-xs text-gray-700 border-l-2 border-orange-300">
                        <strong>Evidence samples:</strong> +{subcat.evidenceList.length} detection{subcat.evidenceList.length > 1 ? 's' : ''} 
                        <span className="text-gray-500 ml-1">(click to view all)</span>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${subcat.positivePercentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-600">{subcat.positivePercentage}%</span>
                    </div>
                    
                    <div className="flex justify-between text-xs text-gray-500">
                      <span className="text-green-600">+{subcat.positive}</span>
                      <span className="text-gray-600">~{subcat.neutral}</span>
                      <span className="text-red-600">-{subcat.negative}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>


        </div>
      )}
    </div>
  );
};

export default CategoryInsightsAnalysis; 